import React, { useState, FC, useEffect } from 'react'
import { t } from '@lingui/macro'
import { Page } from '../../components/common/page/Page'
import PageHeader from '../../components/common/page/header/PageHeader'
import { BlacklistedEmailItem } from './Components/EmailBlacklistItem'
import { ItemList } from '../../components/common/list/ItemList'

import ResettableInput from '../../components/common/form/input/ResettableInput'
import './EmailBlacklistPage.less'
import { ExportEmailBlacklistDocument } from './ExportEmailBlacklist.codegen'
import { useFetchEmailBlacklistQuery } from './FetchEmailBlacklist.codegen'
import { EmailBlocklistExport } from '../../graphql/public/PublicSchemaTypes'
import { DownloadCSVButton } from '../../components/common/download-csv-button/DownloadCSVButton'

const prepareEmailBlocklistCsvRecord = (lines: EmailBlocklistExport[]) =>
  lines.map((line) => ({
    [t`Email`]: line.user.email,
    [t`Display name`]: line.user.display_name,
    [t`First name`]: line.user.first_name,
    [t`Last name`]: line.user.last_name,
    [t`Organisation role`]: line.user.organisation_role,
    [t`Mobile`]: line.user.phone_number,
    [t`Position`]: line.user.position,
    [t`Location`]: line.user.location,
    [t`Department`]: line.user.department,
    [t`Area`]: line.user.area,
    [t`Timezone`]: line.user.timezone,
    [t`Custom 1`]: line.user.custom_1,
    [t`Custom 2`]: line.user.custom_2,
    [t`Custom 3`]: line.user.custom_3,
    [t`Last activity at`]: line.user.last_activity_at,
    [t`Account name`]: line.user.account_name,
    [t`Employee ID`]: line.user.employee_id,
    [t`Smtp response type`]: line.smtp_response_type,
    [t`Smtp response bounce`]: line.smtp_response_bounce,
    [t`Smtp response complaint`]: line.smtp_response_complaint
  }))

export const EmailBlocklistPage: FC = () => {
  const [page, setPage] = useState<number>(1)
  const [search, setSearch] = useState<string>('')
  const pageSizeLimit = 30
  const { data, loading, refetch } = useFetchEmailBlacklistQuery({
    variables: {
      offset: (page - 1) * pageSizeLimit,
      limit: pageSizeLimit,
      search: search ? search : ''
    }
  })
  useEffect(() => {
    refetch()
  }, [search])

  if (!data?.admin?.emailBlacklist) {
    return null
  }

  const { items, count } = data?.admin?.emailBlacklist

  return (
    <Page className="EmailBlacklistPage">
      <PageHeader className="PageHeader" title={t`Email blocklist`} />

      {Array.isArray(items) && (
        <>
          <div className="EmailBlacklist">
            <ItemList
              page={page}
              items={items}
              itemsCount={count as number}
              loading={loading}
              pageSize={pageSizeLimit}
              renderItem={(item) => (
                <BlacklistedEmailItem
                  key={item.id as number}
                  email={item.email as string}
                  user={item.user}
                  notification={item.processedNotification}
                />
              )}
              onPageChange={(page) => {
                setPage(page)
              }}
              header={
                <>
                  <ResettableInput
                    onChange={(search) => {
                      setPage(1)
                      setSearch(search)
                    }}
                    placeholder={t`Search`}
                    title={t`Search`}
                    showResetButton={!!search}
                    showSearchIcon
                    light
                    value={search}
                    onReset={() => {
                      setSearch('')
                    }}
                  />
                </>
              }
              emptyState={t`No blocklisted emails found`}
              actions={
                <DownloadCSVButton
                  fileName={'email-blacklist'}
                  dataPath={'data.admin.organisation.exportEmailBlacklist'}
                  queryOptions={(page, limit) => ({
                    // TODO: These variables are not used in the GQL query!
                    variables: {
                      offset: (page - 1) * limit,
                      limit
                    },
                    query: ExportEmailBlacklistDocument,
                    fetchPolicy: 'no-cache'
                  })}
                  prepareData={prepareEmailBlocklistCsvRecord}
                />
              }
            />
          </div>
        </>
      )}
    </Page>
  )
}
